import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Flame,
  ShieldAlert,
  TrendingUp,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PULSE_TONE = {
  thriving: "bg-[#3F7AD8] text-cream-100",
  healthy: "bg-forest-600 text-cream-100",
  struggles: "bg-[#D19A2E] text-cream-100",
  issues: "bg-terracotta-500 text-cream-100",
  stretched: "bg-[#D19A2E] text-cream-100",
  depleted: "bg-terracotta-500 text-cream-100",
};

const STATUS_LABEL = {
  submitted: "Submitted",
  draft: "In progress",
  missing: "Missing",
};

const HOT_POTATO_LABELS = {
  facilities: "Facilities",
  security: "Security",
  budget: "Budget",
  decision: "Decision",
  staff_volunteer: "Staff / Volunteer",
};

function PulseBadge({ value, size = "sm" }) {
  if (!value) return <span className="text-xs italic text-charcoal-muted">—</span>;
  const tone = PULSE_TONE[value] || "bg-sage-300 text-charcoal";
  const cls =
    size === "xs"
      ? "text-[10px] px-2 py-0.5"
      : "text-xs px-2.5 py-0.5";
  return (
    <span className={`inline-flex rounded-full font-medium capitalize ${cls} ${tone}`}>
      {value}
    </span>
  );
}

function StatusBadge({ status, isLate }) {
  if (status === "submitted" && isLate)
    return <span className="patria-chip-terra text-[10px]" data-testid="rollup-late">LATE</span>;
  if (status === "submitted")
    return <span className="patria-chip-green text-[10px]" data-testid="rollup-submitted">SUBMITTED</span>;
  if (status === "draft")
    return <span className="patria-chip-neutral text-[10px]">IN PROGRESS</span>;
  return <span className="patria-chip-terra text-[10px]" data-testid="rollup-missing">MISSING</span>;
}

function shiftWeek(iso, deltaDays) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

export default function WeeklyWinsRollup() {
  const [tab, setTab] = useState("this-week");
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [history, setHistory] = useState(null);
  const [goal, setGoal] = useState(null);
  const [historyWeeks, setHistoryWeeks] = useState(8);
  const [forbidden, setForbidden] = useState(false);

  const load = async (ws) => {
    setLoading(true);
    try {
      const params = ws ? { week_start: ws } : {};
      const { data } = await api.get("/weekly-wins/rollup/current", { params });
      setRollup(data);
      setWeekStart(data.week_start);
      setGoal(data.goal);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load the roll-up.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (n) => {
    try {
      const { data } = await api.get("/weekly-wins/rollup/history", { params: { weeks: n } });
      setHistory(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => { load(null); }, []);
  useEffect(() => { if (tab === "history") loadHistory(historyWeeks); }, [tab, historyWeeks]);

  const downloadPdf = async () => {
    if (!weekStart) return;
    try {
      const res = await api.get(`/weekly-wins/rollup/pdf`, {
        params: { week_start: weekStart },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `weekly-wins-rollup-${weekStart}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't download the PDF.");
    }
  };

  return (
    <div className="space-y-6" data-testid="weekly-wins-rollup">
      {forbidden ? (
        <div className="max-w-2xl mx-auto patria-card p-8 text-center" data-testid="rollup-forbidden">
          <div className="inline-flex items-center gap-2 patria-chip-terra text-xs">
            <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.5} /> Reviewer Permission Required
          </div>
          <h1 className="mt-3 text-3xl font-serif text-forest-900">
            The Weekly Wins roll-up is for Reviewers
          </h1>
          <p className="mt-2 text-sm text-charcoal-soft">
            People Operations grants this permission in RHYTHM Admin → Weekly Wins Reviewers.
          </p>
          <Link to="/rhythm" className="patria-btn-primary mt-6 inline-flex">
            <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Back To RHYTHM
          </Link>
        </div>
      ) : (
      <>
      <div>
        <Link to="/rhythm" className="text-sm text-forest-700 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To RHYTHM
        </Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
              Reviewer Roll-Up
            </p>
            <h1 className="text-4xl font-serif text-forest-900">Weekly Wins</h1>
            <p className="text-sm text-charcoal-soft mt-1">
              Warm, honest weekly snapshot from each campus. Leader Pulse content is private
              to the submitter, Reviewer, and People Operations.
            </p>
          </div>
          {tab === "this-week" && (
            <button
              type="button"
              onClick={downloadPdf}
              className="patria-btn-secondary text-sm"
              data-testid="btn-rollup-pdf"
            >
              <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Export PDF
            </button>
          )}
        </div>
      </div>

      {goal && (
        <div
          className="patria-card p-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-l-4 border-terracotta-500"
          data-testid="rollup-goal-ribbon"
        >
          <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
            The Goal
          </p>
          <p className="text-sm text-charcoal">
            Improve our Overall Campus Health Index from{" "}
            <strong className="text-forest-900">{goal.current_score}</strong> to{" "}
            <strong className="text-forest-900">{goal.target_score}</strong> by{" "}
            <strong className="text-forest-900">{goal.deadline}</strong>.
          </p>
        </div>
      )}

      <nav className="flex flex-wrap gap-2">
        {[
          { key: "this-week", label: "This Week" },
          { key: "history", label: "Historical Timeline" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`${tab === t.key ? "patria-btn-primary text-sm" : "patria-btn-secondary text-sm"} min-h-[44px]`}
            data-testid={`rollup-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "this-week" && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              className="patria-btn-ghost text-sm"
              onClick={() => load(shiftWeek(weekStart, -7))}
              data-testid="btn-rollup-prev"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Prev Week
            </button>
            <span className="text-sm text-charcoal font-medium" data-testid="rollup-week-label">
              {rollup ? `${rollup.week_start} → ${rollup.week_end}` : "…"}
            </span>
            <button
              type="button"
              className="patria-btn-ghost text-sm"
              onClick={() => load(shiftWeek(weekStart, 7))}
              data-testid="btn-rollup-next"
            >
              Next Week <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          {loading ? (
            <p className="italic text-charcoal-muted" data-testid="rollup-loading">Loading roll-up…</p>
          ) : (
            <ThisWeekTable rows={rollup?.rows || []} />
          )}
        </div>
      )}

      {tab === "history" && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs uppercase tracking-widest text-charcoal-muted">
              Weeks
            </label>
            <select
              className="patria-input"
              value={historyWeeks}
              onChange={(e) => setHistoryWeeks(Number(e.target.value))}
              data-testid="history-weeks-select"
            >
              {[4, 8, 12, 26, 52].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {!history ? (
            <p className="italic text-charcoal-muted">Loading…</p>
          ) : (
            <HistoryStrips history={history} />
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

function ThisWeekTable({ rows }) {
  const [expanded, setExpanded] = useState(null);
  if (rows.length === 0)
    return <p className="italic text-charcoal-muted">No campuses to show.</p>;
  return (
    <div className="space-y-3" data-testid="rollup-this-week">
      {rows.map((r) => {
        const flagged = Object.entries(r.hot_potatoes || {})
          .filter(([, v]) => v && v.flag)
          .map(([k]) => k);
        const isOpen = expanded === r.campus_id;
        return (
          <div
            key={r.campus_id}
            className="patria-card p-4"
            data-testid={`rollup-row-${r.campus_name}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-serif text-xl text-forest-900">{r.campus_name}</p>
                  <StatusBadge status={r.status} isLate={r.is_late} />
                  {flagged.length > 0 && (
                    <span
                      className="patria-chip-terra text-[10px] inline-flex items-center gap-1"
                      data-testid={`rollup-hp-flag-${r.campus_name}`}
                    >
                      <Flame className="h-3 w-3" strokeWidth={2} />
                      {flagged.length} hot potato{flagged.length === 1 ? "" : "es"}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 text-charcoal-muted">
                    Service: <PulseBadge value={r.service_pulse} />
                  </span>
                  <span className="inline-flex items-center gap-1 text-charcoal-muted">
                    Leader: <PulseBadge value={r.leader_pulse} />
                  </span>
                  {r.submitted_by_name && (
                    <span className="text-charcoal-muted italic">
                      by {r.submitted_by_name}
                    </span>
                  )}
                </div>
              </div>
              {r.report_id && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="patria-btn-ghost text-xs"
                    onClick={() => setExpanded(isOpen ? null : r.campus_id)}
                    data-testid={`rollup-expand-${r.campus_name}`}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                    {isOpen ? "Hide" : "Preview"}
                  </button>
                  <Link
                    to={`/rhythm/weekly-wins/reports/${r.report_id}`}
                    className="patria-btn-secondary text-xs"
                    data-testid={`rollup-open-${r.campus_name}`}
                  >
                    Open Full Report
                  </Link>
                </div>
              )}
            </div>
            {isOpen && (
              <div className="mt-4 border-t border-sage-300/60 pt-4 space-y-3 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                    Service Pulse Narrative
                  </p>
                  <p className="text-charcoal-soft italic whitespace-pre-wrap">
                    {r.service_pulse === null ? "—" : "(open the full report to read the narrative)"}
                  </p>
                </div>
                {flagged.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-terracotta-500">
                      Flagged Hot Potatoes
                    </p>
                    <ul className="list-disc pl-5 mt-1">
                      {flagged.map((k) => (
                        <li key={k}>
                          <strong>{HOT_POTATO_LABELS[k]}:</strong>{" "}
                          <span className="italic text-charcoal-muted">
                            {(r.hot_potatoes[k]?.description) || "(no description)"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(r.commitments || []).some((c) => (c.text || "").trim()) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                      Commitments This Week
                    </p>
                    <ul className="list-disc pl-5 mt-1">
                      {(r.commitments || []).filter((c) => (c.text || "").trim()).map((c, i) => (
                        <li key={i}>{c.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HistoryStrips({ history }) {
  const { weeks, strips } = history;
  return (
    <div className="space-y-4" data-testid="rollup-history">
      <div className="patria-card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left pb-2 pr-2">Campus</th>
              <th className="text-center pb-2">Compliance</th>
              {weeks.map((w) => (
                <th key={w} className="text-center pb-2 px-1 text-[10px] font-medium text-charcoal-muted">
                  {w.slice(5)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {strips.map((s) => (
              <tr key={s.campus_id} data-testid={`history-row-${s.campus_name}`}>
                <td className="pr-2 py-1 font-medium text-forest-900">{s.campus_name}</td>
                <td className="text-center py-1" data-testid={`compliance-${s.campus_name}`}>
                  {s.compliance_rate == null ? (
                    <span
                      className="text-[10px] italic text-charcoal-muted"
                      data-testid={`compliance-empty-${s.campus_name}`}
                    >
                      No completed weeks yet
                    </span>
                  ) : (
                    <span
                      className="patria-chip-green"
                      data-testid={`compliance-chip-${s.campus_name}`}
                      title={`${s.compliance_numerator}/${s.compliance_denominator} completed weeks submitted`}
                    >
                      {Math.round(s.compliance_rate * 100)}%
                    </span>
                  )}
                </td>
                {s.timeline.map((t, i) => (
                  <td
                    key={i}
                    className="text-center py-1 px-0.5"
                    data-testid={`strip-${s.campus_name}-${t.week_start}`}
                    data-current-week={t.is_current_week ? "true" : "false"}
                  >
                    <StripCell entry={t} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="patria-card p-4">
        <h3 className="font-serif text-xl text-forest-900 inline-flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-forest-700" strokeWidth={1.5} /> Hot Potato Frequency
        </h3>
        <p className="text-xs text-charcoal-muted italic">
          How often each category was flagged across the last {weeks.length} weeks.
        </p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {strips.map((s) => {
            const total = Object.values(s.hot_potato_frequency || {}).reduce((a, b) => a + b, 0);
            return (
              <div key={s.campus_id} className="p-3 rounded-xl bg-sage-200/40" data-testid={`hp-freq-${s.campus_name}`}>
                <p className="text-forest-900 font-medium">{s.campus_name}</p>
                {total === 0 ? (
                  <p className="text-xs italic text-charcoal-muted">No hot potatoes flagged.</p>
                ) : (
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {Object.entries(s.hot_potato_frequency)
                      .filter(([, v]) => v > 0)
                      .map(([k, v]) => (
                        <li key={k}>
                          <strong>{HOT_POTATO_LABELS[k]}:</strong> {v}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StripCell({ entry }) {
  const isCurrent = entry.is_current_week;
  const isPre = entry.pre_onboarding;
  if (isPre)
    return (
      <span
        className="inline-block w-4 h-4 rounded-sm bg-cream-100 border border-dashed border-sage-300 opacity-60"
        title={`${entry.week_start} · Before this campus onboarded to Weekly Wins`}
      />
    );
  const label = isCurrent ? "In progress · current week" : entry.status;
  if (entry.status === "missing")
    return (
      <span
        className={`inline-block w-4 h-4 rounded-sm ${isCurrent ? "bg-sage-300 border border-dashed border-charcoal-muted" : "bg-terracotta-500/70"}`}
        title={isCurrent ? `${entry.week_start} · ${label}` : "Missing"}
      />
    );
  if (entry.status !== "submitted")
    return (
      <span
        className={`inline-block w-4 h-4 rounded-sm bg-sage-300 ${isCurrent ? "border border-dashed border-charcoal-muted" : ""}`}
        title={isCurrent ? `${entry.week_start} · ${label}` : "In progress"}
      />
    );
  const tone = PULSE_TONE[entry.service_pulse] || "bg-sage-300";
  return (
    <Link
      to={entry.report_id ? `/rhythm/weekly-wins/reports/${entry.report_id}` : "#"}
      title={`${entry.week_start} · Service: ${entry.service_pulse} · Leader: ${entry.leader_pulse}${entry.is_late ? " · LATE" : ""}${isCurrent ? " · current week" : ""}`}
      className={`inline-block w-4 h-4 rounded-sm ${tone} ${entry.is_late ? "ring-2 ring-terracotta-500 ring-offset-1" : ""}`}
    />
  );
}
